import { useContext } from 'react'
import { Conditional } from 'iq-blueberry'
import Img from 'components/common/Img'
import ModalOneButton from 'components/common/ModalOneButton'
import useHasImageError from 'hooks/useHasImageError'
import { UploadContext } from 'pages/credit/BbOurocard/contexts/uploadContext'
import { isMobile } from 'utils/getDevice'
import UploadBlock from '../../components/DocumentsUpload/UploadBlock'
import useIdentityProof from './hook'

import './style.styl'
import { SecurityWarning } from '../../components/SecurityWarning'
import { FormTitle } from 'components/common/FormTitle'
import FormContainer from 'components/common/FormContainer'
import { TakePhotoButtonOptimized } from '../../components/TakePhotoButton/TakePhotoButtonOptimized'
import * as classNames from 'classnames'

const IdentityProof = ({ fireboltStep, frontSideRef = 'user_photo' }) => {
  const { uploadedFilesState } = useContext(UploadContext)

  const modalErrorButtonText = 'Tentar novamente'
  const imageErrorsFound = useHasImageError()
  const hasUploadError = Object.values(imageErrorsFound).find((error) => error)

  const {
    disabledButton,
    modalErrorOpen,
    openCamera,
    setOpenCamera,
    handleImageErrors,
    handleCloseModalError,
    handleStepAfterUpload,
    handleNext,
  } = useIdentityProof({ fireboltStep, frontSideRef, uploadedFilesState })

  handleImageErrors(imageErrorsFound)

  const instructions = (
    <ul>
      <li>Seus olhos devem estar abertos e visíveis</li>
      <li>
        <strong>NÃO devem ser usados óculos</strong> de grau ou escuro
      </li>
      <li>
        <strong>NÃO devem ser usados bonés</strong> ou <strong>chapéus</strong>
      </li>
      <li>
        <strong>Esteja em um local bem iluminado</strong>
      </li>
      <li>De preferencia com uma parede branca de fundo</li>
    </ul>
  )

  return (
    <FormContainer imageSrc="images/bb/identity-proof/upload-block-image.png">
      <div className="identity">
        <div className="identity__content">
          <FormTitle
            title="Selfie para comprovação de identidade"
            description="Essa é uma etapa para a sua segurança. Com uma foto do seu rosto teremos certeza que ninguém está se passando por você."
            isRequiredStep
          />
          <div className="identity__second-section">
            <div className="identity__photo-container">
              <UploadBlock
                optimized
                theme="blue"
                title="Saiba como tirar a foto:"
                desc={
                  <>
                    <Img
                      className="identity__photo-container__ilustration"
                      src="images/bb/identity-proof/upload-block-image.png"
                      alt="Uma imagem com duas fotos de uma mulher com cabelo escuro e cor bronzeada. Na primeira foto a mulher está sorrido, com olhos abertos e rosto virado totalmente para a camera. Há um icone de verificado na cor verde abaixo da primeira foto. Na segunda foto a mulher está sorrindo com o rosto virado de lado. Há um icone de `X` na cor vermelha abaixo da primeira foto."
                    />
                    {instructions}
                  </>
                }
                buttonContinue={<SecurityWarning />}
                goToNextStep={handleNext}
                readyForNextStep={!disabledButton}
                hasUploadError={hasUploadError}
                handleStepAfterUpload={handleStepAfterUpload}
                reviewImageText={
                  'Seus olhos devem estar abertos e visíveis. Óculos escuros, de grau ou chapéus não devem ser usados e a foto deve ser tirada de frente, de preferência com uma parede branca de fundo.'
                }
              />
            </div>
          </div>
        </div>
        <ModalOneButton
          title="Ops, parece que tem algo de errado com a sua foto…"
          text={
            <>
              <div className="identity__modal-container">
                <Img
                  className="identity__modal-image"
                  src="images/bb/identity-proof/upload-block-image.svg"
                  alt="Uma imagem com duas fotos de uma mulher com cabelo escuro e cor bronzeada. Na primeira foto a mulher está sorrido, com olhos abertos e rosto virado totalmente para a camera. Há um icone de verificado na cor verde abaixo da primeira foto. Na segunda foto a mulher está sorrindo com o rosto virado de lado. Há um icone de `X` na cor vermelha abaixo da primeira foto."
                />
                <Conditional
                  condition={isMobile()}
                  renderIf={
                    <p className="identity__modal-text">
                      Pedimos que tente novamente. Relembrando seus olhos devem
                      estar abertos e visíveis. Óculos escuros, de grau ou
                      chapéus não devem ser usados e a fotografia deve ser
                      tirada de frente, de preferência com uma parede branca de
                      fundo.
                    </p>
                  }
                  renderElse={
                    <div className="identity__modal-text--desk">
                      <p>
                        Precisamos que você tente novamente. Relembrando que:
                      </p>
                      {instructions}
                    </div>
                  }
                />
              </div>
              <Conditional
                condition={!isMobile()}
                renderIf={<SecurityWarning />}
              />
            </>
          }
          customButton={
          <TakePhotoButtonOptimized 
            showMask
            maskType="user"
            fileReference="user_photo"
            side="frontUser"
            reviewImageText={'Seus olhos devem estar abertos e visíveis. Óculos escuros, de grau ou chapéus não devem ser usados e a foto deve ser tirada de frente, de preferência com uma parede branca de fundo.'}
            filesReferencesState={uploadedFilesState}
            asUploadError={hasUploadError}
            handleStepAfterUpload={handleStepAfterUpload}
            readyForNextStep={!disabledButton}
            goToNextStep={handleNext}
            onClick={() => setOpenCamera(true)} />}
          isActive={modalErrorOpen}
          handleButton={handleCloseModalError}
          handleCloseModal={handleCloseModalError}
          buttonText={modalErrorButtonText}
          hasOverlay={true}
          hasCloseButton={true}
          customClass={classNames({'open-camera' : openCamera})}
        />
      </div>
    </FormContainer>
  )
}

export default IdentityProof
